import Photo from './Photo';
import YouTube from './YouTube';
import LinkText from './LinkText';
import SmartLink from './SmartLink';
import { parseDesc, parseQuote, prettifyTitle } from '../lib/parse';

/**
 * Generic renderer for sheet rows. Used by the stand-alone pages (EPK, FYC, GPJ,
 * Trio), where the sheet is shown top to bottom exactly as it is written.
 */

const only = (platform) => (platform === 'desktop' ? 'only-desktop' : platform === 'mobile' ? 'only-mobile' : '');

function Picture({ src, href, maxWidth, platform }) {
  const img = <Photo natural src={src} alt="" />;
  return (
    <figure className={`doc-image ${only(platform)}`} style={maxWidth ? { maxWidth } : undefined}>
      {href ? <SmartLink url={href}>{img}</SmartLink> : img}
    </figure>
  );
}

function Card({ card }) {
  const parts = parseDesc(card.desc);
  const image = card.image ? <Photo natural src={card.image} alt="" /> : null;
  return (
    <article className="doc-card">
      {image && <div className="doc-card-art">{card.href ? <SmartLink url={card.href}>{image}</SmartLink> : image}</div>}
      <div className="doc-card-body">
        {card.title && <h3 className="display display--s">{card.title}</h3>}
        {parts.map((p, i) => (p.url
          ? <p key={i}><SmartLink className="inline-link" url={p.url}>{p.label}</SmartLink></p>
          : <p key={i}><LinkText text={p.text} /></p>))}
      </div>
    </article>
  );
}

function Block({ b }) {
  switch (b.kind) {
    case 'subtitle':
      return <h3 className="display display--s">{prettifyTitle(b.text)}</h3>;
    case 'title':
      return <h2 className="display display--m">{prettifyTitle(b.text)}</h2>;
    case 'text': {
      const q = parseQuote(b.text);
      return q ? (
        <blockquote className="press-quote"><p>{q.quote}</p>{q.source && <footer>{q.source}</footer>}</blockquote>
      ) : <p className={`prose ${only(b.platform)}`}><LinkText text={b.text} /></p>;
    }
    case 'link':
      return <p className={only(b.platform)}><SmartLink className="btn btn--line" url={b.url}>{b.label}</SmartLink></p>;
    case 'youtube':
      return <YouTube id={b.id} />;
    case 'soundcloud':
      return (
        <div className="doc-embed">
          <iframe
            title="SoundCloud player"
            height="380"
            scrolling="no"
            loading="lazy"
            src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(b.url)}&color=%23094cb0&auto_play=false&show_teaser=false`}
          />
        </div>
      );
    case 'iframe':
      return (
        <div className="doc-embed doc-embed--form">
          <iframe title="Embedded form" src={b.src} width={b.width || 340} height={b.height || 380} scrolling="no" loading="lazy" />
        </div>
      );
    case 'image':
      return <Picture src={b.src} href={b.href} maxWidth={b.maxWidth} platform={b.platform} />;
    case 'images':
      return (
        <div className={`doc-images ${only(b.platform)}`} style={{ gap: b.gap || 12 }}>
          {b.srcs.map((s) => <Photo key={s} natural src={s} alt="" />)}
        </div>
      );
    case 'imagearray':
      return (
        <div className={`doc-images doc-images--columns ${only(b.platform)}`} style={{ gap: b.gap || 12 }}>
          {b.columns.map((col, i) => (
            <div key={i} className="doc-column" style={{ gap: b.gap || 12 }}>
              {col.map((s) => <SmartLink key={s} url={`/${s.replace(/^\//, '')}`}><Photo natural src={s} alt="" /></SmartLink>)}
            </div>
          ))}
        </div>
      );
    case 'hr':
      return <hr className="doc-rule" />;
    case 'space':
      return <div aria-hidden="true" style={{ height: Math.min(Math.max(b.size / 2, 6), 56) }} />;
    default:
      return null;
  }
}

export default function Chapter({ chapter, hide }) {
  // Group neighbouring cards so they sit side by side.
  const groups = [];
  chapter.blocks.forEach((b) => {
    // pictures already shown elsewhere on the page (e.g. in a page hero)
    if (hide && b.kind === 'image' && hide.has(b.src)) return;
    if (hide && b.kind === 'images' && b.srcs.every((s) => hide.has(s))) return;
    const last = groups[groups.length - 1];
    if (b.kind === 'card') {
      if (last && last.cards) last.cards.push(b); else groups.push({ cards: [b] });
    } else groups.push({ block: b });
  });

  return (
    <section className={`doc-band tone-${chapter.tone}`}>
      <div className="wrap wrap--read doc-flow">
        {chapter.title && (chapter.level === 'subtitle'
          ? <h3 className="display display--s">{prettifyTitle(chapter.title)}</h3>
          : <h2 className="display display--m">{prettifyTitle(chapter.title)}</h2>)}
        {groups.map((g, i) => (g.cards
          ? <div key={i} className="doc-cards">{g.cards.map((c) => <Card key={c.row} card={c} />)}</div>
          : <Block key={g.block.row} b={g.block} />))}
      </div>
    </section>
  );
}
